import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {MutationObserver | null} */
  #mutationGuardObserver = null;

  /** @type {number | null} */
  #mutationGuardSafetyTimer = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener('click', this.#extendCartMutationGuard, true);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener('click', this.#extendCartMutationGuard, true);
    this.#historyAbortController?.abort();
    this.#mutationGuardObserver?.disconnect();
    if (this.#mutationGuardSafetyTimer) clearTimeout(this.#mutationGuardSafetyTimer);
  }

  /**
   * Root cause of the "1 item, no row" drawer bug (reports/overnight-2026-09-03/
   * verify-wave2b.md, section C): removing a line item fires a /cart/change.js
   * request AND, independently, an optimistic CSS-animated row removal
   * (`component-cart-items.js#onLineItemRemove`/`updateQuantity`). The component
   * only disables further clicks (`cart-items-disabled`, pointer-events:none) for
   * the duration of the fetch itself — it re-enables in `finally()` as soon as the
   * response lands, which is often *before* that same response's `morphSection()`
   * call has finished reconciling the DOM and before the row's own removal
   * animation has finished playing. A second remove click landing in that gap
   * fires a second, overlapping /cart/change.js request. Because the two requests'
   * responses can land out of order, whichever one is morphed last wins — even if
   * it's the stale one — leaving the drawer showing a decremented count with a
   * missing/mismatched row. Reopening the drawer forces a fresh full render from
   * the server, which is why it "self-heals."
   *
   * `component-cart-items.js` (where `updateQuantity`/`onLineItemRemove` live) is
   * out of scope for this fix — it's owned by a concurrent agent — so the request
   * itself can't be intercepted or queued from here: the theme's declarative
   * `on:click` dispatcher (`component.js`) listens on `document` in the capture
   * phase, which is the very first node in the capture path and therefore always
   * runs before any listener we attach on a descendant, including this one.
   *
   * What *is* achievable from this file: widen the real, browser-enforced
   * pointer-events guard so it covers the whole mutation — fetch, morph, and the
   * row's exit animation — not just the fetch. That fully prevents a second real
   * click from ever reaching a remove/qty control while the first mutation is
   * still settling, so a second overlapping request can never be sent in the
   * first place. This is a stricter, but safe and file-scope-respecting, form of
   * "serialize cart mutations": no reordering games are needed because there is
   * never more than one in flight.
   *
   * @param {MouseEvent} event
   */
  #extendCartMutationGuard = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('.oss-remove-link, cart-quantity-selector-component button');
    if (!trigger || !this.contains(trigger)) return;

    const cartItemsComponent = trigger.closest('cart-items-component');
    if (!(cartItemsComponent instanceof HTMLElement)) return;

    this.#beginMutationGuard(cartItemsComponent);
  };

  /**
   * Applies our own pointer-events lock on top of (and outliving) the component's
   * native `cart-items-disabled` class, and watches for the underlying mutation to
   * fully settle before lifting it.
   * @param {HTMLElement} cartItemsComponent
   */
  #beginMutationGuard(cartItemsComponent) {
    cartItemsComponent.style.setProperty('pointer-events', 'none');

    this.#mutationGuardObserver?.disconnect();
    if (this.#mutationGuardSafetyTimer) clearTimeout(this.#mutationGuardSafetyTimer);

    const isSettled = () =>
      !cartItemsComponent.classList.contains('cart-items-disabled') &&
      !cartItemsComponent.querySelector('.cart-items__table-row.removing');

    const settle = () => {
      if (!isSettled()) return;
      this.#mutationGuardObserver?.disconnect();
      if (this.#mutationGuardSafetyTimer) clearTimeout(this.#mutationGuardSafetyTimer);
      cartItemsComponent.style.removeProperty('pointer-events');
    };

    // Class toggles (`cart-items-disabled` on/off) and the removal of an animated
    // `.removing` row (a childList change) both need to re-check settle state.
    this.#mutationGuardObserver = new MutationObserver(settle);
    this.#mutationGuardObserver.observe(cartItemsComponent, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    });

    // Safety net: never leave the drawer permanently unclickable if some edge case
    // (network error before disabling, reduced-motion skipping the animation
    // class, etc.) means we never observe a matching mutation.
    this.#mutationGuardSafetyTimer = window.setTimeout(() => {
      this.#mutationGuardObserver?.disconnect();
      cartItemsComponent.style.removeProperty('pointer-events');
    }, 4000);

    // Cover the case where this click's own mutation is already fully settled by
    // the time we observe (e.g. a same-row re-click right at the tail end).
    settle();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
